import React, { useState, useEffect, useMemo, useRef } from "react";
import { FaCopy } from "react-icons/fa";
import { toast } from "react-toastify";
import { C } from "../../../Shared/Brand";
import { TableWrap, TabBar, Pagination, EmptyState } from "../../../Shared/DashboardUI";

function ArbitradeBot() {
  const [wb, setWb] = useState({});
  const [wbo, setWbo] = useState([]);
  const [orderbookError, setOrderbookError] = useState(null);
  const [liveOrders, setLiveOrders] = useState([]);
  const [liveOrdersPage, setLiveOrdersPage] = useState(1);
  const [liveOrdersError, setLiveOrdersError] = useState(null);
  const [orderHash, setOrderHash] = useState([]);
  const [orderHashPage, setOrderHashPage] = useState(1);
  const [orderHashError, setOrderHashError] = useState(null);
  const [activeTab, setActiveTab] = useState("live");

  const CONNECTION_ERROR_MSG =
    "Unable to load live data. The connection to the trading service was blocked or failed — this is often caused by an antivirus/browser security extension (e.g. Bitdefender Web Protection) blocking the connection. Try disabling it or use a different browser/network, then refresh.";
  const [profitHistory, setProfitHistory] = useState([]);
  const [profitHistoryPage, setProfitHistoryPage] = useState(1);
  const [accountStats, setAccountStats] = useState({ balance: 0, withdraw: 0, deposit: 0, profit: 0, swap: 0 });
  const LIVE_ORDERS_PER_PAGE = 10;

  const tickersRef = useRef(null);
  const overviewRef = useRef(null);
  const technicalRef = useRef(null);
  const widgetsLoadedRef = useRef(false);
  useEffect(() => {
    if (!tickersRef.current || !overviewRef.current || !technicalRef.current || widgetsLoadedRef.current)
      return;
    widgetsLoadedRef.current = true;
    const script1 = document.createElement("script");
    script1.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-tickers.js";
    script1.async = true;
    script1.innerHTML = JSON.stringify({
      symbols: [
        {
          description: "Cardano / TetherUS • Binance",
          proName: "BINANCE:ADAUSDT",
        },
        {
          description: "Cardano/Tether • OKK",
          proName: "OKX:ADAUSDT",
        },
        {
          description: "Cardano/Tether • Bybit",
          proName: "BYBIT:ADAUSDT",
        },
      ],
      isTransparent: true,
      showSymbolLogo: true,
      colorTheme: "dark",
      locale: "en",
    });
    tickersRef.current.appendChild(script1);

    // SYMBOL OVERVIEW SCRIPT (Bitcoin / TetherUS - single instance)
    const script2 = document.createElement("script");
    script2.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-symbol-overview.js";
    script2.async = true;
    script2.innerHTML = JSON.stringify({
      symbols: [["BINANCE:BTCUSDT|12M"]],
      chartOnly: false,
      width: "100%",
      height: "499",
      locale: "en",
      colorTheme: "dark",
      autosize: true,
      showVolume: true,
      showMA: true,
      hideDateRanges: false,
      hideMarketStatus: false,
      hideSymbolLogo: false,
      scalePosition: "right",
      scaleMode: "Logarithmic",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif",
      fontSize: "10",
      noTimeScale: false,
      valuesTracking: "3",
      changeMode: "price-and-percent",
      chartType: "candlesticks",
      maLineColor: "#2962FF",
      maLineWidth: 1,
      maLength: 9,
      headerFontSize: "medium",
      backgroundColor: "rgba(19, 23, 34, 0)",
      lineType: 2,
      dateRanges: ["1d|1", "1m|30", "3m|60", "12m|1D", "60m|1W", "all|1M"],
      upColor: "#22ab94",
      downColor: "#f7525f",
      borderUpColor: "#22ab94",
      borderDownColor: "#f7525f",
      wickUpColor: "#22ab94",
      wickDownColor: "#f7525f",
    });
    overviewRef.current.appendChild(script2);

    // TECHNICAL ANALYSIS GAUGE SCRIPT (Bitcoin / TetherUS)
    const script3 = document.createElement("script");
    script3.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-technical-analysis.js";
    script3.async = true;
    script3.innerHTML = JSON.stringify({
      interval: "1h",
      width: "100%",
      isTransparent: true,
      height: "450",
      symbol: "BINANCE:BTCUSDT",
      showIntervalTabs: true,
      displayMode: "single",
      locale: "en",
      colorTheme: "dark",
    });
    technicalRef.current.appendChild(script3);

    const tickersEl = tickersRef.current;
    const overviewEl = overviewRef.current;
    const technicalEl = technicalRef.current;
    return () => {
      if (tickersEl) tickersEl.innerHTML = "";
      if (overviewEl) overviewEl.innerHTML = "";
      if (technicalEl) technicalEl.innerHTML = "";
    };
  }, []);

  useEffect(() => {
    const wsB = new WebSocket(
      `${process.env.REACT_APP_WS_BASE_URI}/api/v1/exchangePrice?symbol=BTC_USDT`,
    );
    wsB.onopen = () => {};
    wsB.onmessage = (msg) => {
      try {
        const data = JSON.parse(msg.data);
        setWb((prev) => {
          const next = { ...prev };
          if (data && typeof data === "object") {
            if (data.BTC_USDT && typeof data.BTC_USDT === "object") {
              next.BTC_USDT = { ...(prev.BTC_USDT || {}), ...data.BTC_USDT };
            }
            Object.keys(data)
              .filter((k) => k !== "BTC_USDT")
              .forEach((k) => {
                next[k] = data[k];
              });
          }
          return next;
        });
      } catch (_) {}
    };
    wsB.onerror = () => {};
    wsB.onclose = () => {};
    return () => {
      wsB.close();
    };
  }, []);

  useEffect(() => {
    const ws = new WebSocket(
      `${process.env.REACT_APP_WS_BASE_URI}/api/v1/sortedOrderbook?symbol=BTC_USDT`,
    );
    let opened = false;
    ws.onopen = () => {
      opened = true;
      setOrderbookError(null);
    };
    ws.onmessage = (msg) => {
      try {
        const parsed = JSON.parse(msg.data);
        const sorted = parsed?.sorted?.data || [];
        if (Array.isArray(sorted) && sorted.length > 0) {
          setWbo(sorted);
          setOrderbookError(null);
        }
      } catch (_) {}
    };
    ws.onerror = () => {
      setOrderbookError(CONNECTION_ERROR_MSG);
    };
    ws.onclose = () => {
      if (!opened) setOrderbookError(CONNECTION_ERROR_MSG);
    };
    return () => {
      ws.close();
    };
  }, []);

  useEffect(() => {
    let stopped = false;
    let wsL = null;
    let reconnectTimer = null;

    const connect = () => {
      wsL = new WebSocket(`${process.env.REACT_APP_WS_BASE_URI}/api/v1/liveOrder`);
      let opened = false;
      wsL.onopen = () => {
        opened = true;
        setLiveOrdersError(null);
      };
      wsL.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);
          if (data && data.type === "init") {
            const orders = Array.isArray(data.orders)
              ? data.orders
              : (data.data ?? data.liveOrder ?? []);

            if (Array.isArray(orders) && orders.length >= 0) {
              setLiveOrders(orders);
              setLiveOrdersError(null);
              const newTotalPages = Math.max(
                1,
                Math.ceil(orders.length / LIVE_ORDERS_PER_PAGE),
              );
              setLiveOrdersPage((prev) => Math.min(prev, newTotalPages));
            }
          }
        } catch (_) {}
      };
      wsL.onerror = () => {
        setLiveOrdersError(CONNECTION_ERROR_MSG);
      };
      // A dropped connection (network blip, idle proxy timeout, etc.) would
      // otherwise freeze the table at its last received batch forever, since
      // nothing here previously retried. Reconnect with a short backoff,
      // mirroring the same close->retry pattern the backend's own exchange
      // connectors use, so the feed self-heals instead of going stale.
      wsL.onclose = () => {
        if (!opened) setLiveOrdersError(CONNECTION_ERROR_MSG);
        if (!stopped) reconnectTimer = setTimeout(connect, 3000);
      };
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsL) wsL.close();
    };
  }, []);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_XPERIA_API_BASE_URI}/api/v1/transactions`,
        );

        if (!response.ok) {
          setOrderHashError(
            `Unable to load transaction history (server returned ${response.status}). Please try again later.`,
          );
          return;
        }

        const parsed = await response.json();

        if (!Array.isArray(parsed)) {
          setOrderHashError(
            "Unable to load transaction history — the server returned an unexpected response.",
          );
          return;
        }

        setOrderHash(parsed);
        setOrderHashError(null);

        const newTotalPages = Math.max(
          1,
          Math.ceil(parsed.length / LIVE_ORDERS_PER_PAGE),
        );

        setOrderHashPage((prev) => Math.min(prev, newTotalPages));
      } catch (err) {
        console.error("❌ Transaction API Fetch Error:", err);
        setOrderHashError(CONNECTION_ERROR_MSG);
      }
    };

    fetchTransactions();
  }, []);

  useEffect(() => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) {
      setProfitHistory([]);
      return;
    }

    fetch(`${process.env.REACT_APP_API_BASE_URI}/api/users/get-roi-history-for-exchange`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((resp) => resp.json())
      .then((res) => {
        if (res && Array.isArray(res.data)) {
          setProfitHistory(res.data);
          setProfitHistoryPage(1);
        } else if (Array.isArray(res)) {
          setProfitHistory(res);
          setProfitHistoryPage(1);
        } else {
          setProfitHistory([]);
        }
      })
      .catch(() => setProfitHistory([]));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
    const base = process.env.REACT_APP_API_BASE_URI;

    Promise.all([
      fetch(`${base}/api/users/get-Profile`, { headers }).then((r) => r.json()).catch(() => null),
      fetch(`${base}/api/users/swap-history?limit=1000`, { headers }).then((r) => r.json()).catch(() => null),
    ]).then(([profileRes, swapRes]) => {
      const user = profileRes?.user || {};
      const swapList = Array.isArray(swapRes?.data) ? swapRes.data : [];
      const swap = swapList.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
      setAccountStats({
        balance: typeof user.currentEarnings === "number" ? user.currentEarnings : 0,
        withdraw: typeof user.totalPayouts === "number" ? user.totalPayouts : 0,
        deposit: typeof user.totalInvestment === "number" ? user.totalInvestment : 0,
        profit: typeof user.totalRoi === "number" ? user.totalRoi : 0,
        swap,
      });
    });
  }, []);

  const profitTotals = useMemo(() => {
    let commission = 0;
    (profitHistory || []).forEach((item) => {
      if (item.type === "ROI" && item.data && typeof item.data.companyCut === "number") {
        commission += item.data.companyCut;
      }
    });
    return { commission };
  }, [profitHistory]);

  const copyToClipboard = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied!");
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <div>
      <div className="xdb-card" style={{ marginBottom: "12px" }}>
        <div ref={tickersRef}></div>
        <div ref={overviewRef}></div>
      </div>

      <div className="xdb-bot-top-grid">
        <div className="xdb-card">
          <h5 style={{ fontSize: "14px", fontWeight: 700, color: C.ltx, marginTop: 0, marginBottom: "12px" }}>BTC/USD Top Exchanges</h5>
          <TableWrap>
            <thead>
              <tr><th>Rank</th><th>Exchange</th><th>Price</th></tr>
            </thead>
            <tbody>
              {(wbo || []).length === 0 ? (
                <tr><td colSpan="3"><EmptyState message={orderbookError || "No exchange data available"} /></td></tr>
              ) : (
                (wbo || []).map((row, ind) => (
                  <tr key={ind}>
                    <td>{ind + 1}</td>
                    <td><img src={`/assets/images/exchange/${row?.logo}`} alt="" style={{ height: 18, verticalAlign: "middle", marginRight: 6 }} />{row.exchange}</td>
                    <td style={{ color: C.up }}>{row.bid}</td>
                  </tr>
                ))
              )}
            </tbody>
          </TableWrap>
        </div>

        <div className="xdb-card">
          <h5 style={{ fontSize: "14px", fontWeight: 700, color: C.ltx, marginTop: 0, marginBottom: "12px" }}>BTC/USDT Technical Analysis</h5>
          <div ref={technicalRef}></div>
        </div>
      </div>

      <div className="xdb-bot-grid">
        <div className="xdb-card">
          <h5 style={{ fontSize: "14px", fontWeight: 700, color: C.ltx, marginTop: 0, marginBottom: "12px" }}>Buy Exchange</h5>
          <TableWrap>
            <thead>
              <tr><th>Exchange</th><th>Size</th><th>Bid</th></tr>
            </thead>
            <tbody>
              {(wbo || []).length === 0 ? (
                <tr><td colSpan="3"><EmptyState message={orderbookError || "No orderbook data available"} /></td></tr>
              ) : (
                (wbo || []).map((row, ind) => (
                  <tr key={row.exchange || ind}>
                    <td><img src={`/assets/images/exchange/${row?.logo}`} alt="" style={{ height: 18, verticalAlign: "middle", marginRight: 6 }} />{row.exchange}</td>
                    <td>{row.bidQty ?? row.size ?? "—"}</td>
                    <td style={{ color: C.up }}>{row.bid}</td>
                  </tr>
                ))
              )}
            </tbody>
          </TableWrap>
        </div>

        <div className="xdb-card">
          <h5 style={{ fontSize: "14px", fontWeight: 700, color: C.ltx, marginTop: 0, marginBottom: "12px" }}>Sell Exchange</h5>
          <TableWrap>
            <thead>
              <tr><th>Exchange</th><th>Size</th><th>Ask</th></tr>
            </thead>
            <tbody>
              {(wbo || []).length === 0 ? (
                <tr><td colSpan="3"><EmptyState message={orderbookError || "No orderbook data available"} /></td></tr>
              ) : (
                (wbo || []).map((row, ind) => {
                  const exchangeData = wb?.BTC_USDT?.[row.exchange] ?? wb?.[row.exchange];
                  return (
                    <tr key={`ask-${row.exchange || ind}`}>
                      <td><img src={`/assets/images/exchange/${row?.logo}`} alt="" style={{ height: 18, verticalAlign: "middle", marginRight: 6 }} />{row.exchange}</td>
                      <td>{exchangeData?.askQty ?? row.askQty ?? row.size ?? "—"}</td>
                      <td style={{ color: C.down }}>{exchangeData?.ask ?? row.ask ?? "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </TableWrap>
        </div>
      </div>

      <div className="xdb-card">
        <TabBar
          tabs={[
            { key: "live", label: "Trading" },
            { key: "profit", label: "Trading Profit" },
            { key: "hash", label: "Orders" },
          ]}
          active={activeTab}
          onChange={setActiveTab}
        />

        {activeTab === "live" && (() => {
          const orders = liveOrders || [];
          const totalPages = Math.max(1, Math.ceil(orders.length / LIVE_ORDERS_PER_PAGE));
          const safePage = Math.min(Math.max(1, liveOrdersPage), totalPages);
          const start = (safePage - 1) * LIVE_ORDERS_PER_PAGE;
          const pageOrders = orders.slice(start, start + LIVE_ORDERS_PER_PAGE);

          if (orders.length === 0) {
            return <EmptyState message={liveOrdersError || "No live trades available"} />;
          }

          return (
            <>
              <TableWrap>
                <thead>
                  <tr>
                    <th>Date</th><th>Currency</th><th>Exchange</th><th>Side</th>
                    <th>Price</th><th>Quantity</th><th>Cost</th><th>Placed by</th>
                  </tr>
                </thead>
                <tbody>
                  {pageOrders.map((order, idx) => {
                    const date = order.date ?? order.createdAt ?? order.timestamp ?? order.time ?? "—";
                    const currency = (order.symbol ?? order.currency ?? "BTC_USDT").replace("_", "/");
                    const exchange = order.exchange ?? order.orderType ?? order.type ?? "—";
                    const logo = order.logo || `${String(exchange).toLowerCase().replace(/\s/g, "")}.svg`;
                    const side = order.side ?? order.type ?? "—";
                    const isBuy = String(side).toLowerCase() === "buy";
                    const price = order.price ?? order.avgPrice ?? "0";
                    const qty = order.quantity ?? order.qty ?? order.amount ?? order.size ?? "0";
                    const cost = order.cost ?? order.total ?? order.value ?? qty;
                    const placedby = order.placedBy ?? order.placedby ?? order.state ?? "—";
                    const img = order.img;
                    return (
                      <tr key={order.id ?? order.orderId ?? start + idx}>
                        <td>{new Date(date).toUTCString().substring("T", 16)} {new Date(date).toLocaleTimeString()}</td>
                        <td>{img && <img src={`/assets/images/exchange/${img}`} alt={currency + " logo"} style={{ height: 20, verticalAlign: "middle", marginRight: 6 }} />}{currency}</td>
                        <td><img src={`/assets/images/exchange/${logo}`} alt="" style={{ height: 18, verticalAlign: "middle", marginRight: 6 }} />{exchange}</td>
                        <td style={{ color: isBuy ? C.up : C.down, fontWeight: 600 }}>{isBuy ? "Buy" : "Sell"}</td>
                        <td><span style={{ color: isBuy ? C.up : C.down }}>{Number(price).toFixed(5)}</span> USDT</td>
                        <td>{qty}</td>
                        <td><span style={{ color: isBuy ? C.up : C.down }}>{Number(cost).toFixed(5)}</span> USDT</td>
                        <td>{placedby}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </TableWrap>
              <Pagination
                page={safePage} totalPages={totalPages}
                from={orders.length === 0 ? 0 : start + 1}
                to={Math.min(start + LIVE_ORDERS_PER_PAGE, orders.length)}
                total={orders.length}
                onFirst={() => setLiveOrdersPage(1)}
                onPrev={() => setLiveOrdersPage((p) => Math.max(1, p - 1))}
                onNext={() => setLiveOrdersPage((p) => Math.min(totalPages, p + 1))}
                onLast={() => setLiveOrdersPage(totalPages)}
              />
            </>
          );
        })()}

        {activeTab === "profit" && (() => {
          const historyRaw = profitHistory || [];

          let allEntries = [];

          historyRaw.forEach((item) => {
            if (item.type === "ROI") {
              allEntries.push({ ...item.data, _type: item.type, _entryType: "daily" });
            }
          });

          allEntries.sort((a, b) => {
            const getDateVal = (entry) => {
              if (entry._entryType === "monthly") {
                return new Date(entry.settledAt || entry.updatedAt || entry.createdAt || 0).getTime();
              } else {
                return new Date(entry.updatedAt || entry.creditedDate || entry.createdAt || 0).getTime();
              }
            };
            return getDateVal(b) - getDateVal(a);
          });

          const orders = allEntries;
          const totalPages = Math.max(1, Math.ceil(orders.length / LIVE_ORDERS_PER_PAGE));
          const safePage = Math.min(Math.max(1, profitHistoryPage), totalPages);
          const start = (safePage - 1) * LIVE_ORDERS_PER_PAGE;
          const pageOrders = orders.slice(start, start + LIVE_ORDERS_PER_PAGE);

          if (orders.length === 0) {
            return <EmptyState message="No records" />;
          }

          const formatNum = (val, n) => (typeof val === "number" ? val.toFixed(n) : val);
          const getTypeLabel = (entry) => {
            if (entry._type === "ROI") return "Trading Profit";
            if (entry._type === "SETTLEMENT") return "Balance";
            return entry._type || "Entry";
          };

          return (
            <>
              <div style={{ display: "flex", flexDirection: "column" }}>
                {pageOrders.map((entry, idx) => {
                  const isMonthly = entry._entryType === "monthly";
                  const key = entry._id || (isMonthly ? `${entry.month}-${entry.year}-${idx}` : `${entry.createdAt}-${idx}`);
                  const baseAmount = isMonthly ? (entry.totalAmount ?? "") : (entry.baseAmount ?? "");
                  const roiAmount = entry.roiAmount !== undefined ? entry.roiAmount : "—";
                  const roiPercent = !isMonthly && entry.roiPercent !== undefined ? entry.roiPercent * 100 : null;
                  const updatedAt = isMonthly
                    ? entry.settledAt || entry.updatedAt || entry.createdAt
                    : entry.updatedAt || entry.creditedDate || entry.createdAt;
                  const formattedDate = updatedAt
                    ? (() => {
                        const d = new Date(updatedAt);
                        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
                      })()
                    : "-";

                  const badge = (
                    <span style={{ background: 'rgba(62,207,142,0.15)', color: C.up, fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px' }}>
                      {getTypeLabel(entry)}
                    </span>
                  );

                  if (isMonthly) {
                    return (
                      <div key={key} className="xdb-card" style={{ padding: '14px 16px', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                          <div>
                            {badge}
                            <div style={{ fontSize: '13px', color: C.ltx2, marginTop: '6px' }}>
                              ({String(entry.month).padStart(2, "0")}/{entry.year})
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: C.up, fontWeight: 700, fontSize: '15px' }}>
                              {typeof roiAmount === "number" ? formatNum(roiAmount, 2) : roiAmount}
                            </div>
                            <div style={{ color: C.ltx3, fontSize: '11px' }}>Investor Profit</div>
                            <div style={{ color: C.ltx3, fontSize: '11px' }}>Withdraw</div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  const seedSrc = String(entry._id || entry.createdAt || entry.updatedAt || idx);
                  let seed = 0;
                  for (let i = 0; i < seedSrc.length; i++) seed = (seed * 31 + seedSrc.charCodeAt(i)) >>> 0;
                  const rand = (seed % 1000) / 1000;
                  const seedSrc2 = seedSrc + "v";
                  let seed2 = 0;
                  for (let i = 0; i < seedSrc2.length; i++) seed2 = (seed2 * 31 + seedSrc2.charCodeAt(i)) >>> 0;
                  const rand2 = (seed2 % 1000) / 1000;
                  const seedSrc3 = seedSrc + "d";
                  let seed3 = 0;
                  for (let i = 0; i < seedSrc3.length; i++) seed3 = (seed3 * 31 + seedSrc3.charCodeAt(i)) >>> 0;

                  const pairOptions = [
                    { symbol: "BTC/USDT", icon: "btc.svg", anchor: 68000 },
                    { symbol: "ETH/USDT", icon: "eth.svg", anchor: 3400 },
                  ];
                  const pair = pairOptions[seed % pairOptions.length];
                  const numericRoi = typeof roiAmount === "number" ? roiAmount : 0;
                  const isProfit = numericRoi >= 0;
                  const isBuy = seed3 % 2 === 0;
                  const pct = typeof roiPercent === "number" ? Math.abs(roiPercent) / 100 : 0;
                  const openPrice = pair.anchor * (1 + (rand - 0.5) * 0.03);
                  const priceRose = isBuy === isProfit;
                  const closePrice = openPrice * (priceRose ? 1 + pct : 1 - pct);
                  const volume = typeof baseAmount === "number"
                    ? Math.max(0.01, +((baseAmount / 100000) * (0.6 + rand2 * 0.8)).toFixed(2))
                    : baseAmount;

                  const formattedTime = updatedAt
                    ? new Date(updatedAt).toLocaleTimeString("en-GB", { hour12: false })
                    : "";

                  return (
                    <div key={key} style={{ padding: '12px 4px', borderBottom: `1px solid ${C.dbdr}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '8px' }}>
                        <div>
                          <span style={{ color: C.ltx, fontWeight: 700, fontSize: '15px' }}>{pair.symbol.replace("/", "")}</span>
                          <span style={{ color: isBuy ? C.blue : C.down, fontWeight: 700, fontSize: '15px', marginLeft: '6px' }}>{isBuy ? "buy" : "sell"}</span>
                          <span style={{ color: C.ltx, fontWeight: 700, fontSize: '15px', marginLeft: '6px' }}>{volume}</span>
                        </div>
                        <div style={{ color: isProfit ? C.blue : C.down, fontWeight: 700, fontSize: '15px' }}>
                          {isProfit ? "+" : ""}
                          {formatNum(numericRoi, 2)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginTop: '4px', fontSize: '13px', color: C.ltx3 }}>
                        <div>{openPrice.toFixed(2)} &rarr; {closePrice.toFixed(2)}</div>
                        <div>{formattedDate} {formattedTime}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Pagination
                page={safePage} totalPages={totalPages}
                from={orders.length === 0 ? 0 : start + 1}
                to={Math.min(start + LIVE_ORDERS_PER_PAGE, orders.length)}
                total={orders.length}
                onFirst={() => setProfitHistoryPage(1)}
                onPrev={() => setProfitHistoryPage((p) => Math.max(1, p - 1))}
                onNext={() => setProfitHistoryPage((p) => Math.min(totalPages, p + 1))}
                onLast={() => setProfitHistoryPage(totalPages)}
              />
            </>
          );
        })()}

        {activeTab === "hash" && (() => {
          const withdrawalOrders = orderHash || [];
          const totalPages = Math.max(1, Math.ceil(withdrawalOrders.length / LIVE_ORDERS_PER_PAGE));
          const safePage = Math.min(Math.max(1, orderHashPage), totalPages);
          const start = (safePage - 1) * LIVE_ORDERS_PER_PAGE;
          const pageOrders = withdrawalOrders.slice(start, start + LIVE_ORDERS_PER_PAGE);

          if (withdrawalOrders.length === 0) {
            return (
              <EmptyState message={orderHashError || "No transaction records"} />
            );
          }

          return (
            <>
              <TableWrap>
                <thead>
                  <tr><th>Tx Hash</th><th>From</th><th>To</th><th>Amount</th><th>Block</th></tr>
                </thead>
                <tbody>
                  {pageOrders.map((order, idx) => {
                    const d = order;
                    return (
                      <tr key={d?._id ?? start + idx}>
                        <td>
                          {d.tx ? `${d.tx.slice(0, 13)}...` : "—"}
                          {d.tx && (
                            <button type="button" title="Copy ID to clipboard" onClick={() => copyToClipboard(d.tx)}
                              style={{ background: 'none', border: 'none', color: C.primary, cursor: 'pointer', marginLeft: 8 }}>
                              <FaCopy />
                            </button>
                          )}
                        </td>
                        <td>
                          {d.from ? `${d.from.slice(0, 10)}...${d.from.slice(-9)}` : "—"}
                          {d.from && (
                            <button type="button" title="Copy From address to clipboard" onClick={() => copyToClipboard(d.from)}
                              style={{ background: 'none', border: 'none', color: C.primary, cursor: 'pointer', marginLeft: 8 }}>
                              <FaCopy />
                            </button>
                          )}
                        </td>
                        <td>
                          {d.to ? `${d.to.slice(0, 10)}...${d.to.slice(-9)}` : "—"}
                          {d.to && (
                            <button type="button" title="Copy To address to clipboard" onClick={() => copyToClipboard(d.to)}
                              style={{ background: 'none', border: 'none', color: C.primary, cursor: 'pointer', marginLeft: 8 }}>
                              <FaCopy />
                            </button>
                          )}
                        </td>
                        <td>{d.amount ?? "—"}</td>
                        <td>{d.block ?? "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </TableWrap>
              <Pagination
                page={safePage} totalPages={totalPages}
                from={withdrawalOrders.length === 0 ? 0 : start + 1}
                to={Math.min(start + LIVE_ORDERS_PER_PAGE, withdrawalOrders.length)}
                total={withdrawalOrders.length}
                onFirst={() => setOrderHashPage(1)}
                onPrev={() => setOrderHashPage((p) => Math.max(1, p - 1))}
                onNext={() => setOrderHashPage((p) => Math.min(totalPages, p + 1))}
                onLast={() => setOrderHashPage(totalPages)}
              />
            </>
          );
        })()}

      </div>
    </div>
  );
}

export default ArbitradeBot;
